/**
 * ENG-7360 — detect a transient LLM-API error in a pane.log TAIL (manager-side).
 *
 * ENG-6861's friendly transient-error rewrite (packages/mcp transient-api-error
 * `rewriteTransientApiError`) only fires on the ghost-reply egress path, where
 * the raw "API Error: 529 …" text becomes the scraped last-assistant-turn and
 * the channel posts it. But when a 529 (or 429/503/500) EXHAUSTS the retry
 * budget (attempt N/N) the turn WEDGES, the manager force-fresh respawns the
 * session, and the channel deflects `cause=wedged` — no reply text is ever
 * produced, so that rewrite never runs and the user is left in silence
 * (ENG-7360, live on Phil/agt-aws-1, Telegram).
 *
 * The manager (apps/cli) does not import @integrity-labs/augmented-mcp — like
 * the deflection-counter reader in responsiveness-probe.ts it re-implements the
 * small pure check against the same contract so the wedge-respawn path can tell
 * whether the wedge followed a transient API error and, if so, surface the
 * friendly "please resend" notice the clean path already gives (via the ENG-6058
 * give-up signal, tagged `reason: 'transient_overload'`).
 *
 * This is the sibling of mcp's `detectTransientApiError`: that one is anchored at
 * the START of a scraped reply (position 0); this one scans a LOG tail, where the
 * error sits mid-stream with leading tmux/box-drawing/ANSI noise (e.g.
 * "⎿  API Error: 529 {…}"). It re-anchors each line before applying the same
 * strict matcher, so it never loosens detection — a line must still carry an
 * "API Error: <code>", a "<code> <transient phrase>", or an
 * "overloaded_error"/"rate_limit_error" type token to match.
 *
 * Pure, allocation-bounded (only the last `maxLines` are inspected), never throws.
 */

/** The transient server-side error classes we recognise (mirrors mcp CODE_KIND). */
export type TransientErrorKind = 'rate_limit' | 'server_error' | 'unavailable' | 'overloaded';

export interface TransientApiErrorMatch {
  /** The HTTP-ish status code detected (e.g. "529"). */
  code: string;
  /** Short class label for telemetry/log aggregation. */
  kind: TransientErrorKind;
}

/**
 * Transient server-side codes only. Auth/billing codes (401/402/403) are
 * excluded by omission — those need operator action, not a "please resend".
 * Kept byte-for-byte in sync with packages/mcp `transient-api-error.ts` CODE_KIND;
 * the two live apart only because apps/cli must not depend on the mcp package.
 */
const CODE_KIND: Readonly<Record<string, TransientErrorKind>> = {
  '429': 'rate_limit',
  '500': 'server_error',
  '503': 'unavailable',
  '529': 'overloaded',
};

/** Strip ANSI CSI escape sequences (colour/cursor) a TUI writes into pane.log. */
function stripAnsi(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x1b\[[0-9;]*[A-Za-z]/g, '');
}

/**
 * Apply the strict, START-anchored transient-error match to a single candidate
 * string (already de-noised). Mirrors mcp `detectTransientApiError` exactly.
 */
function matchAnchored(candidate: string): TransientApiErrorMatch | null {
  const head = candidate.replace(/^\s+/, '').slice(0, 200);

  // (a) "API Error: <code> …"  (b) "<code> <transient phrase>" at the start.
  const code =
    head.match(/^api error:\s*(\d{3})\b/i)?.[1] ??
    head.match(
      /^(\d{3})\s+(?:overloaded|service unavailable|internal server error|too many requests|server error)\b/i,
    )?.[1];
  if (code) {
    const kind = CODE_KIND[code];
    if (kind) return { code, kind };
  }

  // (c) Anthropic error-type token at the start (optionally quote/JSON-prefixed).
  const typeToken = head.match(/^[^a-z0-9]*(overloaded_error|rate_limit_error)\b/i)?.[1];
  if (typeToken) {
    return typeToken.toLowerCase() === 'overloaded_error'
      ? { code: '529', kind: 'overloaded' }
      : { code: '429', kind: 'rate_limit' };
  }

  return null;
}

/**
 * Scan the tail of a pane.log for a transient LLM-API error line. Returns the
 * match CLOSEST TO THE TAIL (the most recent error, i.e. the one that plausibly
 * wedged the current turn), or null when none is found.
 *
 * Each line is de-noised (ANSI stripped) and re-anchored at the first
 * "API Error:" / error-type token so leading tmux/box-drawing glyphs don't
 * defeat the START-anchored matcher — without ever loosening what counts as a
 * match (the transient phrase / type token is still required).
 */
export function detectTransientApiErrorInLog(
  logTail: string,
  opts: { maxLines?: number } = {},
): TransientApiErrorMatch | null {
  if (!logTail) return null;
  const maxLines = opts.maxLines ?? 200;
  const lines = logTail.split(/\r?\n/);
  const start = Math.max(0, lines.length - maxLines);
  for (let i = lines.length - 1; i >= start; i--) {
    const line = stripAnsi(lines[i] ?? '').replace(/^\s+/, '');
    if (!line) continue;
    // Candidate anchors: the whole trimmed line, plus the substring from the
    // first "API Error:" / type-token occurrence (handles "⎿  API Error: 529 …").
    const candidates = [line];
    const idxApiErr = line.search(/api error:/i);
    if (idxApiErr > 0) candidates.push(line.slice(idxApiErr));
    const idxType = line.search(/(?:overloaded_error|rate_limit_error)\b/i);
    if (idxType > 0) candidates.push(line.slice(idxType));
    for (const candidate of candidates) {
      const match = matchAnchored(candidate);
      if (match) return match;
    }
  }
  return null;
}
